import { getFormattedFullAddress } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';

import { CONTACT_GROUP_ORDER, ContactSourceKind } from 'constants/contactSources';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getContactGroup, getContactSourceLabel } from 'utils/contactSources';

import {
  BORROWER_DRIVE_TEXT,
  NOT_APPLICABLE_TEXT,
  NOT_MAPPED_TEXT,
  NOT_SENT_TEXT,
  PARTY_EDIT_LOCATIONS,
  PARTY_GROUP_LABELS,
  PartyDriveState,
  PENDING_CONTACT_LABELS,
  UNNAMED_PARTY_TEXT,
} from './Parties.constants';

const emptyMatch = { sentAs: [], isUnmapped: false, overflowFamilies: [] };

/**
 * The key a /contacts/v2 party and a DRIVE preview entry share. A source with no stored id (a
 * portal realtor typed on the application) falls back to its system key.
 * @param {{ sourceKind?: string|null, sourceId?: string|null, systemKey?: string|null }} item
 * @returns {string}
 */
export const getPartyMatchKey = ({ sourceKind = null, sourceId = null, systemKey = null }) =>
  `${sourceKind ?? ''}|${String(sourceId ?? systemKey ?? '').toLowerCase()}`;

const addToIndex = (index, item, update) => {
  const key = getPartyMatchKey(item);

  return { ...index, [key]: update(index[key] ?? emptyMatch) };
};

/**
 * Indexes the preview by party so each row can say what it will be sent as. The built-in
 * participant set (mapping off) carries no source, so nothing matches and the index is empty.
 * @param {Object|null} preview the drive-report/preview response
 * @returns {Object<string, { sentAs: string[], isUnmapped: boolean, overflowFamilies: string[] }>}
 */
export const indexDrivePreview = preview => {
  if (!preview?.mappingEnabled) {
    return {};
  }

  const withParticipants = (preview.participants ?? []).reduce(
    (index, participant) =>
      addToIndex(index, participant, match => ({
        ...match,
        sentAs: _.uniq([...match.sentAs, participant.participantType]),
      })),
    {},
  );

  const withUnmapped = (preview.unmapped ?? []).reduce(
    (index, party) => addToIndex(index, party, match => ({ ...match, isUnmapped: true })),
    withParticipants,
  );

  return (preview.overflow ?? []).reduce(
    (index, party) =>
      addToIndex(index, party, match => ({
        ...match,
        overflowFamilies: _.uniq([...match.overflowFamilies, party.familyCode]),
      })),
    withUnmapped,
  );
};

/**
 * @param {Object} row a party row from getPartyRows
 * @param {{ preview: Object|null, previewIndex: Object }} args
 * @returns {{ state: string, text: string }}
 */
export const getPartyDriveStatus = (row, { preview, previewIndex }) => {
  if (row.sourceKind === ContactSourceKind.BORROWER) {
    return { state: PartyDriveState.BORROWER, text: BORROWER_DRIVE_TEXT };
  }

  if (!preview) {
    return { state: PartyDriveState.PENDING, text: NOT_APPLICABLE_TEXT };
  }

  if (!preview.mappingEnabled) {
    return { state: PartyDriveState.MAPPING_DISABLED, text: NOT_APPLICABLE_TEXT };
  }

  const match = previewIndex[row.matchKey] ?? emptyMatch;

  if (match.sentAs.length) {
    return { state: PartyDriveState.SENT, text: match.sentAs.join(' + ') };
  }

  if (match.overflowFamilies.length) {
    return {
      state: PartyDriveState.OVERFLOW,
      text: `Not sent: no ${match.overflowFamilies.join(', ')} slot left`,
    };
  }

  if (match.isUnmapped) {
    return { state: PartyDriveState.NOT_MAPPED, text: NOT_MAPPED_TEXT };
  }

  return { state: PartyDriveState.NOT_SENT, text: NOT_SENT_TEXT };
};

const getPersonName = ({ firstName, lastName }) =>
  [firstName, lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');

const getTypeLabel = ({ contactTypeName, roleName, externalContactType }) =>
  contactTypeName || roleName || externalContactType?.value || externalContactType || '';

const getPartyDetail = (party, personName) =>
  [
    personName ? party.companyName : null,
    party.nmlsId ? `NMLS ${party.nmlsId}` : null,
    party.stateLicenseId ? `License ${party.stateLicenseId}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * @param {Object|null} address a loan-contacts view address; its state is { abbreviation, name }
 * @returns {string} e.g. "12 Main St, Suite 4, Austin, TX 78701"; empty when there is none
 */
export const getContactAddressText = address =>
  address
    ? getFormattedFullAddress({
        address: {
          street: [address.street, address.suite].filter(Boolean).join(', '),
          city: address.city,
          state: address.state?.abbreviation,
          zip: address.zip,
        },
      })
    : '';

const isLoanFileContact = party => party.sourceKind === ContactSourceKind.LOAN_FILE_CONTACT;

/**
 * @param {Object} party a /contacts/v2 party
 * @returns {{ label: string, pathParams: Object }|null} where a party from another part of the
 * loan is edited; null for a loan-file contact (edited here) or a source with no editor
 */
export const getPartyEditLocation = party => {
  const location = isLoanFileContact(party) ? null : PARTY_EDIT_LOCATIONS[party.sourceKind];

  return location
    ? { label: location.label, pathParams: location.getPathParams(party.sourceId ?? null) }
    : null;
};

const toLowerId = id => String(id ?? '').toLowerCase();

const toLoanFileParty = contact => ({
  firstName: contact.fullName?.firstName ?? null,
  lastName: contact.fullName?.lastName ?? null,
  emailAddress: contact.email ?? null,
  phoneNumber: contact.phoneNumber ?? null,
  contactTypeName: contact.contactTypeName ?? null,
  companyName: contact.companyName ?? null,
  nmlsId: contact.nmlsId ?? null,
  stateLicenseId: contact.stateLicenseId ?? null,
  sourceKind: ContactSourceKind.LOAN_FILE_CONTACT,
  sourceId: contact.id,
});

/**
 * The parties endpoint reads the saved loan, so it misses a loan-file contact still waiting as a
 * pending add and shows old values for a pending edit. The loan-contacts view has the current
 * state, so it fills in the former and overrides the latter.
 * @param {Array<Object>} parties
 * @param {Array<Object>} contactsView
 * @returns {Array<Object>} parties with the loan-file contacts brought up to date
 */
const withCurrentLoanFileContacts = (parties, contactsView) => {
  const contactsById = _.keyBy(contactsView, ({ id }) => toLowerId(id));
  const savedContactIds = new Set(
    parties.filter(isLoanFileContact).map(p => toLowerId(p.sourceId)),
  );

  return [
    ...parties.map(party => {
      const contact = isLoanFileContact(party) ? contactsById[toLowerId(party.sourceId)] : null;

      return contact
        ? { ...party, ..._.omitBy(toLoanFileParty(contact), _.isNil), sourceId: party.sourceId }
        : party;
    }),
    ...contactsView.filter(({ id }) => !savedContactIds.has(toLowerId(id))).map(toLoanFileParty),
  ];
};

/**
 * Maps /contacts/v2/{appId}/parties to table rows. The endpoint lists the loan officer twice (as
 * the loan's officer and as a loan-role assignment), so exact repeats collapse into one row. A
 * loan-file contact's address and current values come from the loan-contacts view (its id is the
 * party's sourceId).
 * @param {Array<Object>} parties
 * @param {Array<Object>} [contactsView] /applications/{id}/contacts/view
 * @returns {Array<Object>}
 */
export const getPartyRows = (parties, contactsView = []) => {
  const contactsById = _.keyBy(contactsView, ({ id }) => toLowerId(id));

  return _.uniqBy(
    withCurrentLoanFileContacts(parties, contactsView).filter(party => !party.isDeleted),
    party => `${getPartyMatchKey(party)}|${getPersonName(party)}|${party.emailAddress ?? ''}`,
  ).map((party, index) => {
    const personName = getPersonName(party);
    const matchKey = getPartyMatchKey(party);
    const contactId = isLoanFileContact(party) ? (party.sourceId ?? null) : null;
    const contact = contactsById[toLowerId(contactId)] ?? null;

    return {
      id: `${matchKey}|${index}`,
      matchKey,
      sourceKind: party.sourceKind ?? null,
      contactId,
      name: personName || party.companyName || party.emailAddress || UNNAMED_PARTY_TEXT,
      detail: getPartyDetail(party, personName),
      contactType: getTypeLabel(party),
      source: getContactSourceLabel(party.sourceKind) ?? '',
      email: party.emailAddress || null,
      phone: party.phoneNumber || null,
      address: getContactAddressText(contact?.address ?? null),
      pendingLabel: PENDING_CONTACT_LABELS[contact?.pendingChangesActionType] ?? null,
      isPendingRemoval: contact?.pendingChangesActionType === PendingChangesActionType.DELETED,
      editLocation: getPartyEditLocation(party),
      group: getContactGroup(party),
    };
  });
};

/**
 * @param {Array<Object>} rows
 * @returns {Array<{ group: string, title: string, rows: Array<Object> }>} non-empty groups in order
 */
export const groupPartyRows = rows =>
  CONTACT_GROUP_ORDER.map(group => ({
    group,
    title: PARTY_GROUP_LABELS[group],
    rows: rows.filter(row => row.group === group),
  })).filter(({ rows: groupRows }) => groupRows.length > 0);

/**
 * @param {number} count
 * @returns {string}
 */
export const getPartiesCountTitle = count =>
  count === 1 ? '1 contact on this loan' : `${count} contacts on this loan`;

/**
 * @param {Object|null} preview
 * @returns {string|null} e.g. "2 not mapped · 1 over the slot limit"; null when nothing is left out
 */
export const getPreviewIssueSummary = preview => {
  const unmappedCount = preview?.unmapped?.length ?? 0;
  const overflowCount = preview?.overflow?.length ?? 0;

  const parts = [
    unmappedCount ? `${unmappedCount} not mapped` : null,
    overflowCount ? `${overflowCount} over the slot limit` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : null;
};

/**
 * @param {string} name the contact's row name
 * @returns {string} the remove-confirmation description
 */
export const getRemoveContactDescription = name => `Remove ${name} from this loan's contacts?`;

/**
 * @param {Array<Object>} rows from getPartyRows
 * @returns {Array<Object>} the rows without loan-file contacts, for when auto-lock hides them
 */
export const excludeLoanFileRows = rows => rows.filter(({ contactId }) => !contactId);
